import { Link, useParams } from "react-router-dom";
import { Truck, CarFront, Battery, MountainSnow, Users, DollarSign, Briefcase, Star, Car, Banknote } from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
import { SectionHeader, Hero } from "../components/UI";
import LiveInventory from "../components/LiveInventory";
import { siteConfig } from "../config";

const HUB_CARDS = {
  byType: [
    ["Trucks", "/shop-by-need/trucks", <Truck />],
    ["SUVs", "/shop-by-need/suvs", <CarFront />],
    ["Sedans", "/used-vehicles/cars", <Car />],
    ["EVs", "/shop-by-need/evs", <Battery />],
    ["AWD & 4x4", "/shop-by-need/awd-4x4", <MountainSnow />],
    ["Work Vehicles", "/shop-by-need/work-vehicles", <Briefcase />],
  ],
  byBudget: [
    ["Under $20,000", "/used-vehicles/under-20000"],
    ["Under $25,000", "/used-vehicles/under-25000"],
    ["Under $30,000", "/inventory?max_price=30000"],
    ["Low Payment Options", "/shop-by-need/payment"],
    ["Lease Help", "/lease-help"],
  ],
  byLifestyle: [
    ["Family Vehicles", "/shop-by-need/family-vehicles"],
    ["First-Time Buyer Picks", "/shop-by-need/first-time-buyer"],
    ["Daily Commuters", "/shop-by-need/commuter-vehicles"],
    ["Ohio Winter Vehicles", "/shop-by-need/awd-4x4"],
    ["Work Trucks", "/shop-by-need/work-vehicles"],
    ["Road Trip Vehicles", "/shop-by-need/suvs"],
  ],
  byFeature: [
    "Heated Seats", "Remote Start", "Third Row", "Apple CarPlay", "Android Auto",
    "Leather", "Sunroof", "Backup Camera", "Blind Spot Monitoring",
  ],
};

const DETAIL_CONTENT = {
  trucks: { icon: <Truck size={32} />, title: "I Need a Truck", desc: "From midsize to full-size — including off-road Trail Boss and Z71 options.", link: "/inventory?body_style=truck", inv: { body_style: "truck" } },
  suvs: { icon: <CarFront size={32} />, title: "I Need an SUV", desc: "From compact Trax to three-row Suburban — Scott helps you compare size, capability, and cost.", link: "/inventory?body_style=suv", inv: { body_style: "suv" } },
  evs: { icon: <Battery size={32} />, title: "I Want an EV", desc: "Range, charging, lease vs buy, and Ohio-specific advice on EV ownership.", link: "/inventory?body_style=ev", inv: { body_style: "ev" } },
  "awd-4x4": { icon: <MountainSnow size={32} />, title: "I Need AWD or 4x4", desc: "AWD and 4WD options for Ohio winters and weekend adventures.", link: "/inventory?drivetrain=awd-4x4", inv: { drivetrain: "awd-4x4" } },
  "family-vehicles": { icon: <Users size={32} />, title: "Family Vehicles", desc: "Third-row seating, safety features, and family-friendly tech.", link: "/inventory?body_style=suv", inv: { body_style: "suv" } },
  payment: { icon: <Banknote size={32} />, title: "Shopping by Payment", desc: "Tell me your target monthly payment — I'll help you plan around it.", link: "/payment-help", inv: { status: "used", max_price: 30000 } },
  "first-time-buyer": { icon: <Star size={32} />, title: "First-Time Buyer Picks", desc: "Affordable, dependable, easy-to-finance options.", link: "/used-vehicles/under-25000", inv: { status: "used", max_price: 25000 } },
  "work-vehicles": { icon: <Briefcase size={32} />, title: "Work Vehicles", desc: "Trucks and vans built for the job — capability, capacity, comfort.", link: "/new-vehicles/commercial", inv: { body_style: "commercial" } },
  "commuter-vehicles": { icon: <Car size={32} />, title: "Commuter Vehicles", desc: "Efficient, low-maintenance, easy on gas.", link: "/used-vehicles/cars", inv: { status: "used", body_style: "car" } },
};

export function ShopByNeedHub() {
  return (
    <main id="main-content">
      <Seo
        title="Shop Vehicles by Need in Central Ohio | Scott Sells Chevys"
        description="Shop new and used vehicles by what you need. Find trucks, SUVs, EVs, AWD vehicles, family vehicles, work vehicles, and low payment options."
        canonicalPath="/shop-by-need"
        jsonLd={[breadcrumbs([{ name: "Home", path: "/" }, { name: "Shop by Need", path: "/shop-by-need" }])]}
      />
      <Hero title="Shop by What You Need" subtitle="Skip the generic browsing. Tell me what matters and I'll help you focus." />
      <section className="ssc-section">
        <div className="ssc-container space-y-12">
          <Block title="Shop by Vehicle Type" items={HUB_CARDS.byType.map(([t,h,i]) => ({title: t, to: h, icon: i}))} />
          <Block title="Shop by Budget" items={HUB_CARDS.byBudget.map(([t,h]) => ({title:t,to:h}))} />
          <Block title="Shop by Lifestyle" items={HUB_CARDS.byLifestyle.map(([t,h]) => ({title:t,to:h}))} />
          <div>
            <SectionHeader title="Shop by Feature" />
            <div className="flex flex-wrap gap-2">
              {HUB_CARDS.byFeature.map((f) => (
                <Link key={f} to={`/inventory?search=${encodeURIComponent(f)}`} className="ssc-chip">{f}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

const Block = ({ title, items }) => (
  <div>
    <SectionHeader title={title} />
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((it) => (
        <Link key={it.title} to={it.to} className="ssc-card flex items-start gap-3" data-testid={`sbn-${it.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          {it.icon && <div className="text-[var(--ssc-blue)]">{it.icon}</div>}
          <div className="font-extrabold text-[var(--ssc-navy)]">{it.title}</div>
        </Link>
      ))}
    </div>
  </div>
);

export function ShopByNeedDetail() {
  const { topic } = useParams();
  const c = DETAIL_CONTENT[topic];
  if (!c) {
    return (
      <main id="main-content" className="ssc-container ssc-section">
        <Seo title="Shop by Need | Scott Sells Chevys" description="Shop vehicles by what you need." />
        <h1 className="text-3xl font-extrabold text-[var(--ssc-navy)]">Shop by Need</h1>
        <p className="mt-3 text-[var(--ssc-ink-2)]">Pick a need from the menu or visit the <Link className="underline" to="/shop-by-need">Shop by Need hub</Link>.</p>
      </main>
    );
  }
  return (
    <main id="main-content">
      <Seo
        title={`${c.title} | Scott Sells Chevys`}
        description={c.desc}
        canonicalPath={`/shop-by-need/${topic}`}
        jsonLd={[breadcrumbs([{ name: "Home", path: "/" }, { name: "Shop by Need", path: "/shop-by-need" }, { name: c.title, path: `/shop-by-need/${topic}` }])]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-4 rounded-2xl">{c.icon}</div>
            <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">{c.title}</h1>
          </div>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">{c.desc}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to={c.link} className="ssc-btn ssc-btn-primary">Browse Matches</Link>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-red">Have Scott Find One</Link>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-ghost">Text Scott</a>
          </div>
        </div>
      </section>
      <section className="ssc-section">
        <div className="ssc-container">
          <LiveInventory
            title="Live Matches Right Now"
            subtitle="Real vehicles from the live inventory feed that fit this need."
            params={c.inv || {}}
            limit={4}
            viewAllTo={c.link.startsWith("/inventory") || c.link.startsWith("/new-vehicles") || c.link.startsWith("/used-vehicles") ? c.link : "/inventory"}
            testId={`sbn-live-${topic}`}
          />
        </div>
      </section>
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 gap-6">
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">How Scott can help</h2>
            <p className="text-[var(--ssc-ink-2)] mt-2">Tell me a few details about what you need and I'll narrow down real options. No pressure — just real comparisons across new and used.</p>
          </div>
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Quick next steps</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Shop New</Link>
              <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Shop Used</Link>
              <Link to="/value-my-trade" className="ssc-btn ssc-btn-outline">Value My Trade</Link>
              <Link to="/scotts-deal-desk" className="ssc-btn ssc-btn-outline">Build My Own Deal</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
